<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<template>
  <div :class="b()">
    <!-- Logo section -->
    <!-- <div :class="b('logo')">
      <span :class="b('logo-text')">Forever Studios</span>
    </div> -->

    <!-- Navigation items -->
    <nav :class="b('menu')">
      <div
        v-for="item in allItems"
        :key="item.key"
        :class="b('item')"
        @mouseenter="handleMouseEnter(item)"
        @focusin="handleMouseEnter(item)"
      >
        <router-link
          v-if="item.link && !item.children"
          :to="item.link"
          :class="b('link', {
            active: activeMenu === item.key,
          })"
        >
          <span :class="b('link-text')">{{ item.title }}</span>
        </router-link>

        <button
          v-else
          type="button"
          :class="b('link', {
            active: activeMenu === item.key,
            open: isOpen(item),
            'has-children': item.children,
          })"
        >
          <span :class="b('link-text')">{{ item.title }}</span>
        </button>

        <!-- First level dropdown -->
        <transition name="dropdown">
          <div
            v-if="item.children && isOpen(item)"
            :class="b('dropdown')"
            @mouseenter="handleDropdownEnter(item)"
            @focusin="handleDropdownEnter(item)"
          >
            <div
              v-for="child in item.children"
              :key="child.key"
              :class="b('dropdown-item')"
              @mouseenter="handleChildMouseEnter(child)"
              @focusin="handleChildMouseEnter(child)"
            >
              <router-link
                v-if="child.link && !child.children"
                :to="child.link"
                :class="
                  b('dropdown-link', { active: activeSubmenu?.value === child.key })
                "
              >
                {{ child.title }}
              </router-link>

              <button
                v-else
                type="button"
                :class="
                  b('dropdown-link', {
                    active: activeSubmenu?.value === child.key,
                    'has-children': child.children,
                  })
                "
              >
                {{ child.title }}
              </button>

              <!-- Second level dropdown (horizontal) -->
              <transition name="dropdown-slide">
                <div
                  v-if="child.children && isChildOpen(child)"
                  :class="b('dropdown-nested')"
                  @mouseenter="handleNestedDropdownEnter(child)"
                  @focusin="handleNestedDropdownEnter(child)"
                >
                  <router-link
                    v-for="subChild in child.children"
                    :key="subChild.key"
                    :to="subChild.link"
                    :class="b('dropdown-link', { active: activeSubmenu.value === subChild.key })"
                  >
                    {{ subChild.title }}
                  </router-link>
                </div>
              </transition>
            </div>
          </div>
        </transition>
      </div>
    </nav>

    <!-- Right section -->
    <div :class="b('right')">
      <ThemeToggle />
      <button
        :class="b('logout')"
        @click="handleLogout"
      >
        Logout
      </button>
    </div>
  </div>
</template>
<script setup>
import {
  ref, onMounted, onUnmounted, computed,
} from 'vue';
import { useStore } from 'vuex';
import ThemeToggle from '@/components/ux/navigation/ThemeToggle.vue';
import bem from '@/utils/bem';
import pageUrls from '@/router/pageUrls';

const b = bem('navigation');
const store = useStore();

const isAdmin = computed(() => {
  const { role, id } = store.state.auth;
  return role === 'admin' || role === 'Admin' || id === 1;
});

// State management
const activeMenu = ref(null); // Currently hovered/active main menu
const activeSubmenu = ref(null); // Currently hovered/active submenu

// Handlers
const handleMouseEnter = (item) => {
  if (activeMenu.value !== item.key) {
    activeMenu.value = item.key;
    activeSubmenu.value = null;
  }
};

const handleChildMouseEnter = (child) => {
  if (child.children) {
    // Set this as active submenu
    activeSubmenu.value = child.key;
  }
};

// Prevent closing dropdown when mouse is over it
const handleDropdownEnter = (item) => {
  // Keep the menu active when hovering dropdown
  activeMenu.value = item.key;
};

const handleNestedDropdownEnter = (child) => {
  // Keep the submenu active when hovering nested dropdown
  activeSubmenu.value = child.key;
};

const isOpen = (item) => activeMenu.value === item.key;
const isChildOpen = (child) => activeSubmenu.value === child.key;

// Logout handler
const handleLogout = () => {
  store.dispatch('auth/logout');
};

// Click outside handler
const handleClickOutside = ({ target }) => {
  const navElement = target.closest('.navigation');
  if (!navElement) {
    activeMenu.value = null;
    activeSubmenu.value = null;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const menuItems = [
  {
    key: 'UserAdminPage',
    title: 'Users',
    link: pageUrls.usersPage,
  },
  {
    key: 'WizardsPage',
    title: 'Wizards',
    link: '',
    children: [
      {
        key: 'DigitizePage',
        title: 'Digitize',
        link: '',
        children: [
          {
            title: 'Tape Transfer',
            link: `${pageUrls.digitizePage}/tape_transfer`,
            key: 'tape_transfer',
          },
          {
            title: 'Film Transfer',
            link: `${pageUrls.digitizePage}/film_transfer`,
            key: 'film_transfer',
          },
          {
            title: 'Audio Transfer',
            link: `${pageUrls.digitizePage}/audio_transfer`,
            key: 'audio_transfer',
          },
          {
            title: 'Digital Transfer',
            link: `${pageUrls.digitizePage}/digital_transfer`,
            key: 'digital_transfer',
          },
          {
            title: 'Photo scanning',
            link: `${pageUrls.digitizePage}/photo_scan`,
            key: 'photo_scan',
          },
          {
            title: 'Forever cloud',
            link: `${pageUrls.digitizePage}/forever_cloud`,
            key: 'forever_cloud',
          },
        ],
      },
      {
        key: 'EnhancePage',
        title: 'Enhance',
        children: [
          {
            title: 'Photo Restoration/Colorization',
            link: `${pageUrls.enhancePage}/photo_restoration`,
            key: 'photo_restoration',
          },
          {
            title: 'Photo Retouching',
            link: `${pageUrls.enhancePage}/photo_retouching`,
            key: 'photo_retouching',
          },
        ],
      },
      {
        key: 'CreatePage',
        title: 'Create',
        children: [
          {
            title: 'Create templates',
            link: `${pageUrls.createPage}`,
            key: 'createTemplates',
          },
          {
            title: 'Collections',
            link: `${pageUrls.collectionsPage}`,
            key: 'createCollections',
          },
        ],
      },
    ],
  },
  {
    key: 'DeliveriesPage',
    title: 'Deliveries',
    link: pageUrls.deliveriesPage,
  },
  {
    key: 'GiftCardPage',
    title: 'Gift Cards',
    link: '',
    children: [
      {
        title: 'Gift Cards',
        key: 'gift_cards',
        children: [
          {
            title: 'Gift Card',
            link: `${pageUrls.giftCardPage}/gift_card`,
            key: 'gift_card',
          },
          {
            title: 'Gift images',
            link: `${pageUrls.giftCardPage}/images`,
            key: 'gift_images',
          },
          {
            title: 'Gift wrapper',
            link: `${pageUrls.giftCardPage}/wrapper`,
            key: 'gift_wrapper',
          },
        ],
      },
    ],
  },
  {
    key: 'ZipCodesPage',
    title: 'Zip Codes',
    link: pageUrls.zipCodesPage,
  },
  {
    key: 'CompaniesPage',
    title: 'PODs',
    link: pageUrls.companiesPage,
  },
  {
    key: 'PromotionsPage',
    title: 'Promotions',
    link: pageUrls.promotionsPage,
  },
  {
    key: 'RushesPage',
    title: 'Rushes',
    link: pageUrls.rushesPage,
  },
  {
    key: 'HelpCenter',
    title: 'Help Center',
    link: pageUrls.helpCenterPage,
  },
  {
    key: 'PermissionsPage',
    title: 'Permissions',
    link: pageUrls.permissionsPage,
  },
  {
    key: 'BlogsPage',
    title: 'Blogs',
    children: [
      {
        title: 'Categories',
        link: `${pageUrls.blogsPage}/categories`,
        key: 'categories',
      },
      {
        title: 'Articles',
        link: `${pageUrls.blogsPage}/articles`,
        key: 'articles',
      },
    ],
  },
  {
    key: 'SettingsPage',
    title: 'Settings',
    children: [
      {
        title: 'Statuses',
        link: `${pageUrls.settingsPage}/statuses`,
        key: 'statuses',
      },
      {
        title: 'Emails',
        link: `${pageUrls.settingsPage}/emails`,
        key: 'emails',
      },
      {
        title: 'Sms Templates',
        link: `${pageUrls.settingsPage}/sms`,
        key: 'sms',
      },
      {
        title: 'Messages Templates',
        link: `${pageUrls.settingsPage}/messagesTemplates`,
        key: 'messagesTemplates',
      },
    ],
  },
  {
    key: 'pagesContent',
    title: 'Pages Content',
    link: pageUrls.pagesContent,
  },
  {
    key: 'ErrorLogsPage',
    title: 'Error Logs',
    link: pageUrls.errorLogsPage,
    adminOnly: true,
  },
  {
    key: 'AnalyticsPage',
    title: 'Analytics',
    adminOnly: true,
    children: [
      {
        title: 'Dashboard',
        link: pageUrls.analyticsDashboard,
        key: 'analyticsDashboard',
      },
      {
        title: 'Sessions',
        link: pageUrls.sessionsList,
        key: 'sessionsList',
      },
      {
        title: 'Heatmaps',
        link: pageUrls.heatmapsPage,
        key: 'heatmapsPage',
      },
      {
        title: 'Errors',
        link: pageUrls.frontendErrorsPage,
        key: 'frontendErrorsPage',
      },
      {
        title: 'Alerts',
        link: pageUrls.alertsPage,
        key: 'alertsPage',
      },
      {
        title: 'API Keys',
        link: pageUrls.analyticsApiKeysPage,
        key: 'analyticsApiKeysPage',
      },
    ],
  },
];

const allItems = computed(() => menuItems.filter((item) => !item.adminOnly || isAdmin.value));
</script>

<style scoped lang="scss">
@import 'src/styles/media.scss';

.navigation {
  display: flex;
  width: 100vw;
  height: 80px;
  background: var(--Dark, #2B3544);
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
  transition: background-color 0.3s ease;
  position: relative;
  z-index: 1000;
  font-family: 'Montserrat', sans-serif;

  @include mediaMinMax(1025, $normal) {
    height: vw-full(80);
    padding: vw-full(0) vw-full(12);
  }

  &__logo {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-right: 32px;

    @include mediaMinMax(1025, $normal) {
      margin-right: vw-full(32);
    }
  }

  &__logo-text {
    font-size: 20px;
    font-weight: 700;
    color: var(--Accent, #E8C88F);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(20);
    }
  }

  &__menu {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    justify-content: center;
    height: 100%;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(4);
    }
  }

  &__item {
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 20px;
    color: var(--Text-Inverse, #ffffff);
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    position: relative;
    height: 100%;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(6);
      padding: vw-full(12) vw-full(20);
      font-size: vw-full(14);
    }

    &_active {
      color: var(--Dark, #293241);
      font-size: 16px;
      font-weight: 600;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(16);
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 92px;
        background: var(--Accent, #E8C88F);
        border-radius: 0 0 20px 20px;
        z-index: 1;

        @include mediaMinMax(1025, $normal) {
          height: vw-full(92);
          border-radius: 0 0 vw-full(20) vw-full(20);
        }
      }
    }
  }

  &__link-text {
    z-index: 3;
  }

  &__arrow {
    font-size: 10px;
    transition: transform 0.3s ease;
    margin-left: 4px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(10);
      margin-left: vw-full(4);
    }

    &--rotated {
      transform: rotate(180deg);
    }
  }

  &__arrow-right {
    font-size: 10px;
    margin-left: auto;
    padding-left: 12px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(10);
      padding-left: vw-full(12);
    }
  }

  &__dropdown {
    position: absolute;
    top: 0;
    left: 0;
    background: var(--Background-Main, #ffffff);
    border-radius: 12px;
    z-index: 0;
    padding-top: 92px;
    border-radius: 0 0 20px 20px;
    border: 1px solid var(--gold, #ECC486);
    background: #FFF;
    box-shadow: 3px 3px 6px 0 rgba(95, 138, 164, 0.20);
    width: 100%;

    @include mediaMinMax(1025, $normal) {
      border-radius: vw-full(12);
      padding-top: vw-full(92);
      border-radius: 0 0 vw-full(20) vw-full(20);
      box-shadow: vw-full(3) vw-full(3) vw-full(6) 0 rgba(95, 138, 164, 0.20);
    }
  }

  &__dropdown-item {
    position: relative;
  }

  &__dropdown-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 6px 0;
    color: var(--Text-Primary, #2B3544);
    text-decoration: none;
    font-size: 16px;
    font-weight: 500;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;
    color: var(--light-gray, #7F7F7F);

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(6) 0;
      font-size: vw-full(16);
    }

    &:hover {
      color: var(--Dark, #293241);
    }

    &_active {
      color: var(--Dark, #293241);
    }

  }

  &__dropdown-nested {
    position: absolute;
    top: -6px;
    left: calc(100% - 4px);
    min-width: 220px;
    background: var(--Background-Main, #ffffff);
    border: 1px solid var(--Border-Color, #E5E7EB);
    border-radius: 12px;
    box-shadow: var(--Shadow-Large, 0 8px 16px rgba(43, 53, 68, 0.2));
    overflow: hidden;
    z-index: 1002;
    padding-left: 8px;

    @include mediaMinMax(1025, $normal) {
      top: vw-full(-6);
      left: calc(100% - vw-full(4));
      min-width: vw-full(220);
      border-radius: vw-full(12);
      box-shadow: var(--Shadow-Large, 0 vw-full(8) vw-full(16) rgba(43, 53, 68, 0.2));
      padding-left: vw-full(8);
    }
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
    }
  }

  &__logout {
    width: 116px;
    height: 48px;
    flex-shrink: 0;
    border-radius: 12px;
    border: 1px solid var(--Light-Blue, #D7E8F0);
    background: var(--Dark, #293241);
    color: var(--Light-Blue, #D7E8F0);
    text-align: center;
    font-family: Montserrat;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    cursor: pointer;
    transition: all 0.3s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(116);
      height: vw-full(48);
      border-radius: vw-full(12);
      font-size: vw-full(20);
    }

    &:hover {
      background: var(--Light-Blue, #D7E8F0);
      color: var(--Dark, #293241);
    }

    &:active {
      transform: scale(0.98);
    }
  }
}

// Dropdown animations
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-12px);

  @include mediaMinMax(1025, $normal) {
    transform: translateY(vw-full(-12));
  }
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);

  @include mediaMinMax(1025, $normal) {
    transform: translateY(vw-full(-8));
  }
}

// Nested dropdown slide animation
.dropdown-slide-enter-active,
.dropdown-slide-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-slide-enter-from {
  opacity: 0;
  transform: translateX(-12px);

  @include mediaMinMax(1025, $normal) {
    transform: translateX(vw-full(-12));
  }
}

.dropdown-slide-leave-to {
  opacity: 0;
  transform: translateX(-8px);

  @include mediaMinMax(1025, $normal) {
    transform: translateX(vw-full(-8));
  }
}
</style>
